/**
 * Κάρτα/IRIS τραπεζιού που δεν κατέληξε σε πληρωμή, όπως τη στέλνει ο server στο GET …/tables/:id/details
 * (session.card_doubt — backend utils/dineInCardDoubt). ΙΔΙΟ κείμενο σε ταμείο και PDA.
 *
 * Μόνο ένδειξη (αποφάσεις ιδιοκτήτη 25/10/2026): κανένα κουμπί επιβεβαίωσης, καμία φραγή πληρωμής. Υπάρχει
 * γιατί στις 25/10 (GENERIS, τραπέζι 21, 116 €) ο ταμίας έκανε F5 όσο περίμενε το τερματικό: το «κοίτα το
 * τερματικό» δεν έφτασε ποτέ και το τραπέζι φαινόταν απλήρωτο, ενώ η κάρτα είχε περάσει. Τώρα η χρέωση
 * φαίνεται στο τραπέζι από τον server, και μετά από ανανέωση.
 */

export type CardDoubtState = 'in_flight' | 'uncertain' | 'charged_unrecorded';

export interface CardDoubt {
  transactionId: number;
  state: CardDoubtState;
  amount: number;
  method: 'card' | 'iris';
  /** «00:12», ώρα Ελλάδας — από τη βάση */
  at: string | null;
  terminalName: string | null;
  ageSeconds: number | null;
}

/** Κάθε πόσο ξαναφορτώνεται το τραπέζι όσο μια χρέωση τρέχει ακόμη σε άλλη συσκευή. */
export const CARD_DOUBT_POLL_MS = 5000;

const STATES: readonly CardDoubtState[] = ['in_flight', 'uncertain', 'charged_unrecorded'];

/** Οι χρεώσεις του session.card_doubt· ό,τι δεν μοιάζει με χρέωση (παλιός server, λάθος σχήμα) αγνοείται. */
export function cardDoubtsOf(session: { card_doubt?: unknown } | null | undefined): CardDoubt[] {
  const list = session?.card_doubt;
  if (!Array.isArray(list)) return [];
  return list.filter((d: any): d is CardDoubt =>
    !!d && STATES.includes(d.state) && Number.isFinite(Number(d.amount)));
}

export const hasCardInFlight = (doubts: CardDoubt[]): boolean => doubts.some(d => d.state === 'in_flight');

const eur = (n: number): string => `${Number(n).toFixed(2).replace('.', ',')} €`;

/** Τίτλος και οδηγία για ΜΙΑ χρέωση. */
export function cardDoubtText(d: CardDoubt): { title: string; detail: string } {
  const iris = d.method === 'iris';
  const what = iris ? `IRIS ${eur(d.amount)}` : `Κάρτα ${eur(d.amount)}`;
  const terminal = d.terminalName ? ` «${d.terminalName}»` : '';
  const when = d.at ? ` (${d.at})` : '';
  if (d.state === 'in_flight') {
    return {
      title: `${what} σε εξέλιξη στο τερματικό${terminal}${when}`,
      detail: 'Περίμενε την απάντηση του τερματικού — μη χρεώσεις ξανά και μην πάρεις μετρητά για αυτό το ποσό.',
    };
  }
  if (d.state === 'charged_unrecorded') {
    return {
      title: `${iris ? 'Η πληρωμή IRIS' : 'Η κάρτα'} ${eur(d.amount)}${when} ΠΕΡΑΣΕ, αλλά δεν καταγράφηκε στο τραπέζι`,
      detail: 'ΜΗΝ χρεώσεις ξανά — ενημέρωσε την υποστήριξη να την καταχωρίσει.',
    };
  }
  return {
    title: `Αβέβαιη ${iris ? 'πληρωμή IRIS' : 'κάρτα'} ${eur(d.amount)}${when}${terminal ? ` — τερματικό${terminal}` : ''}`,
    detail: 'Το τερματικό δεν έδωσε τελική απάντηση και η χρέωση ΜΠΟΡΕΙ να έγινε. Κοίτα το τερματικό ή το απόκομμα '
      + 'πριν χρεώσεις ξανά· αν πέρασε, ΜΗΝ ξαναχρεώσεις και ενημέρωσε την υποστήριξη.',
  };
}
